/**
 * Legacy auth API compatibility layer for portal-ui.
 *
 * All consumers in portal-ui/src import from this module instead of directly
 * from `aws-amplify/auth`, keeping legacy call patterns used across components
 * while the underlying implementation relies on Amplify.
 *
 */
import { SessionStorage } from '@base-shared/utils/session-storage';
import Utils from '@/globals/utils.js';
import { useCognitoAuthStore } from '@base-shared/stores/cognito-auth';

export const COGNITO_STORAGE_PREFIX = 'CognitoIdentityServiceProvider';

/*
 * Callers that use setUserSessionToLocalStorage expect:
 *   { idToken: { jwtToken, payload }, accessToken: { jwtToken, payload },
 *     refreshToken: { token }, isValid() }
 *
 * Amplify V6 does not expose the refresh token through the public API, so it is
 * read directly from the default Amplify localStorage key:
 *   CognitoIdentityServiceProvider.{clientId}.{username}.refreshToken
 */
/**
 * @param {string} [refreshTokenOverride] - When provided (SSO token-injection
 *   path), use this refresh token directly instead of reading from localStorage.
 *   Avoids a race where localStorage hasn't been written yet and ensures
 *   Amplify's own token refresh can succeed on the very first call.
 * @deprecated - This function is only used for legacy compatibility with the
 * old Cognito auth API. New code should use the Amplify Auth API directly.
 */
export async function buildLegacySessionFromAmplify (refreshTokenOverride?: string, cognitoIdP?: Record<string, string>) {
  const { tokens } = await useCognitoAuthStore().fetchAndStoreAuthSession();
  if (!tokens) {
    throw new Error('No tokens available in Amplify auth session');
  }

  if (!tokens.idToken || !tokens.accessToken) {
    throw new Error('ID token or access token is missing from Amplify auth session');
  }

  const idTokenStr = tokens.idToken.toString();
  const accessTokenStr = tokens.accessToken.toString();

  // Use the clientId from the access token payload (Cognito 'client_id' claim).
  const clientId = tokens.accessToken.payload['client_id'];

  // Always use LastAuthUser as the lookup key
  const lastAuthUser = await SessionStorage.getItem(
    `${COGNITO_STORAGE_PREFIX}.${clientId}.LastAuthUser`
  );
  const refreshToken = refreshTokenOverride ??
    (lastAuthUser && await SessionStorage.getItem(
      `${COGNITO_STORAGE_PREFIX}.${clientId}.${lastAuthUser}.refreshToken`
    )) ??
    '';

  const legacySessionObject = {
    idToken: { jwtToken: idTokenStr, payload: tokens.idToken.payload },
    accessToken: { jwtToken: accessTokenStr, payload: tokens.accessToken.payload },
    refreshToken: { token: refreshToken },
    isValid () { return true; },
    loginType: cognitoIdP?.SSOProviderName ? 'external' : 'native',
    sso: cognitoIdP?.SSOProviderName
      ? Utils.encodeBase64Format({
        stage: cognitoIdP?.CognitoStage,
        clientId: cognitoIdP?.CognitoClientAppID
      })
      : null,
  }
  return legacySessionObject;
}
